import React, { FC, useMemo } from 'react'
import styled from '@emotion/styled'
import { useTranslation } from 'react-i18next'
import { PencilIcon, TrashIcon } from 'lucide-react'
import { ChatMobilePreviewDetailData } from './ChatMobilePreviewDetail'
import { ChatMobilePreviewDetails } from './ChatMobilePreviewDetails'
import { MoneyView } from '../../../common/components/MoneyView'
import { ChatPreviewComment } from '../../preview/ChatPreviewComment'
import { PreviewAutocomplete } from '../../../features/preview-autocomplete'
import { ApiCatalogSearchItem } from '../../../api/catalog/types'
import { ChatPreviewIconButton } from '../../preview/ChatPreviewIconButton'
import { PreviewItem } from '../../../features/preview-edits/model/types'
import { PreviewSkeleton } from '../../../features/preview-edits/ui/PreviewSkeleton'
import { PreviewUnitSelect } from '../../../features/preview-edits/ui/PreviewUnitSelect'
import { PreviewQuantityInput } from '../../../features/preview-edits/ui/PreviewQuantityInput'
import { PreviewEditableValue } from '../../../features/preview-edits/ui/PreviewEditableValue'
import { PreviewDateInput } from '../../../features/preview-edits/ui/PreviewDateInput'

const MobileInput = styled('div')(({ theme }) => ({
  '& input, & select': {
    maxWidth: 100,
    padding: `${theme.spacing.sm}px ${theme.spacing.md}px`,
    border: `1px solid ${theme.colors.border.default}`,
    borderRadius: 4,
    textAlign: 'left',
  },
}))

interface ChatMobilePreviewItemProps {
  item: PreviewItem
  editing: boolean
  onEditToggle: () => void
  onSelect: (selection: ApiCatalogSearchItem) => void
  onRemove: () => void
  onStartEditingQuantity: () => void
  onStopEditingQuantity: () => void
  onUnitChange: (unitIndex: number) => void
  onQuantityChange: (quantity: number) => void
  isAgent: boolean
  onDateChange: (date: string | undefined) => void
}

export const ChatMobilePreviewItem: FC<ChatMobilePreviewItemProps> = ({
  item,
  editing,
  onEditToggle,
  onSelect,
  onRemove,
  onStartEditingQuantity,
  onStopEditingQuantity,
  onUnitChange,
  onQuantityChange,
  isAgent,
  onDateChange,
}) => {
  const { t } = useTranslation()

  if (item.type === 'original') {
    return (
      <ChatMobilePreviewOriginalItem
        item={item}
        editing={editing}
        onEditToggle={onEditToggle}
        onSelect={onSelect}
        onRemove={onRemove}
        onStartEditingQuantity={onStartEditingQuantity}
        onStopEditingQuantity={onStopEditingQuantity}
        onUnitChange={onUnitChange}
        onQuantityChange={onQuantityChange}
        isAgent={isAgent}
        onDateChange={onDateChange}
      />
    )
  }

  const rownum = item.type === 'edited' ? item.previewItem.rownum : undefined
  const selectedUnitIndex = item.catalogItem.unitsOfMeasure.indexOf(item.unitsOfMeasure.type)
  const details: ChatMobilePreviewDetailData[] = [
    ...(rownum ? [{ title: t('#'), value: rownum }] : []),
    { title: t('Description'), value: item.catalogItem.description },
    {
      title: t('Quantity'),
      value: (
        <MobileInput>
          <PreviewQuantityInput
            value={item.unitsOfMeasure.value}
            integer={item.unitsOfMeasure.type === 'units'}
            onChange={onQuantityChange}
          />
        </MobileInput>
      ),
    },
    {
      title: t('Units'),
      value: (
        <MobileInput>
          <PreviewUnitSelect
            units={item.catalogItem.unitsOfMeasure}
            selectedIndex={selectedUnitIndex}
            onChange={onUnitChange}
          />
        </MobileInput>
      ),
    },
    { title: t('Unit Price'), value: <PreviewSkeleton /> },
    { title: t('% Discount'), value: <PreviewSkeleton /> },
    { title: t('Subtotal'), value: <PreviewSkeleton /> },
    {
      title: t('Delivery Date'),
      value: isAgent ? (
        <MobileInput>
          <PreviewDateInput value={item.date} onChange={onDateChange} />
        </MobileInput>
      ) : (
        <PreviewSkeleton />
      ),
    },
  ]

  return (
    <>
      <ChatMobilePreviewDetails details={details} />

      {editing && (
        <PreviewAutocomplete
          autoFocus
          defaultTokens={item.catalogItem.tokens}
          originalRequest={item.type === 'edited' ? item.previewItem.originalRequest : undefined}
          searchRequest={item.type === 'edited' ? item.previewItem.searchRequest : undefined}
          onCancel={onEditToggle}
          onSelect={onSelect}
        />
      )}

      <div style={{ display: 'flex', gap: 8 }}>
        {!editing && (
          <ChatPreviewIconButton onClick={onEditToggle}>
            <PencilIcon size={16} /> {t('Edit')}
          </ChatPreviewIconButton>
        )}

        <ChatPreviewIconButton onClick={onRemove}>
          <TrashIcon size={16} /> {t('Remove')}
        </ChatPreviewIconButton>
      </div>
    </>
  )
}

const ChatMobilePreviewOriginalItem: FC<ChatMobilePreviewItemProps> = ({
  item: previewItem,
  editing,
  onEditToggle,
  onSelect,
  onRemove,
  onStartEditingQuantity,
  onStopEditingQuantity,
  onUnitChange,
  onQuantityChange,
  isAgent,
  onDateChange,
}) => {
  const { t } = useTranslation()
  const originalItem = previewItem as Extract<PreviewItem, { type: 'original' }>
  const apiItem = originalItem.previewItem

  const details: ChatMobilePreviewDetailData[] = useMemo(
    () =>
      apiItem.quotedItem
        ? [
            { title: t('#'), value: apiItem.rownum },
            { title: t('Description'), value: apiItem.quotedItem.description },
            ...(originalItem.changedUnitsOfMeasure
              ? [
                  {
                    title: t('Quantity'),
                    value: (
                      <MobileInput>
                        <PreviewQuantityInput
                          value={originalItem.changedUnitsOfMeasure.value}
                          integer={originalItem.changedUnitsOfMeasure.type === 'units'}
                          onChange={onQuantityChange}
                          autoFocus
                        />
                      </MobileInput>
                    ),
                  },
                  {
                    title: t('Units'),
                    value: (
                      <MobileInput>
                        <PreviewUnitSelect
                          units={apiItem.quotedItem.skuUnitsOfMeasure}
                          selectedIndex={apiItem.quotedItem.skuUnitsOfMeasure.indexOf(
                            originalItem.changedUnitsOfMeasure.type,
                          )}
                          onChange={onUnitChange}
                        />
                      </MobileInput>
                    ),
                  },
                  { title: t('Unit Price'), value: <PreviewSkeleton /> },
                  { title: t('% Discount'), value: <PreviewSkeleton /> },
                  { title: t('Subtotal'), value: <PreviewSkeleton /> },
                  {
                    title: t('Delivery Date'),
                    value: isAgent ? (
                      <MobileInput>
                        <PreviewDateInput
                          value={originalItem.changedDate ?? apiItem.quotedItem.date}
                          onChange={onDateChange}
                        />
                      </MobileInput>
                    ) : (
                      <PreviewSkeleton />
                    ),
                  },
                ]
              : [
                  {
                    title: t('Units'),
                    value: (
                      <PreviewEditableValue onClick={onStartEditingQuantity}>
                        {apiItem.quotedItem.units}
                      </PreviewEditableValue>
                    ),
                  },
                  {
                    title: t('Quantity'),
                    value: (
                      <PreviewEditableValue onClick={onStartEditingQuantity}>
                        {apiItem.quotedItem.quantity}
                      </PreviewEditableValue>
                    ),
                  },
                  { title: t('Unit Price'), value: <MoneyView value={apiItem.quotedItem.price} /> },
                  { title: t('% Discount'), value: apiItem.quotedItem.discount },
                  { title: t('Subtotal'), value: <MoneyView value={apiItem.quotedItem.subtotal} /> },
                  {
                    title: t('Delivery Date'),
                    value: isAgent ? (
                      <MobileInput>
                        <PreviewDateInput
                          value={originalItem.changedDate ?? apiItem.quotedItem.date}
                          onChange={onDateChange}
                        />
                      </MobileInput>
                    ) : (
                      apiItem.quotedItem.date
                    ),
                  },
                ]),
          ]
        : [
            { title: t('#'), value: apiItem.rownum },
            { title: t('Original request'), value: apiItem.originalRequest },
          ],
    [
      t,
      apiItem,
      originalItem.changedUnitsOfMeasure,
      originalItem.changedDate,
      onStartEditingQuantity,
      onUnitChange,
      onQuantityChange,
      isAgent,
      onDateChange,
    ],
  )

  const showAutocomplete = !apiItem.quotedItem || editing
  const editingQuantity = !!originalItem.changedUnitsOfMeasure && !editing

  return (
    <>
      <div
        onBlur={
          editingQuantity
            ? (e) => {
                if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
                  onStopEditingQuantity()
                }
              }
            : undefined
        }
      >
        <ChatMobilePreviewDetails details={details} />
      </div>

      {showAutocomplete && (
        <PreviewAutocomplete
          autoFocus={editing}
          defaultTokens={apiItem.quotedItem?.tokens}
          originalRequest={apiItem.originalRequest}
          searchRequest={apiItem.searchRequest}
          onCancel={editing ? onEditToggle : undefined}
          onSelect={onSelect}
        />
      )}

      <div style={{ display: 'flex', gap: 8 }}>
        {!showAutocomplete && (
          <ChatPreviewIconButton onClick={onEditToggle}>
            <PencilIcon size={16} /> {t('Edit')}
          </ChatPreviewIconButton>
        )}

        <ChatPreviewIconButton onClick={onRemove}>
          <TrashIcon size={16} /> {t('Remove')}
        </ChatPreviewIconButton>
      </div>

      {apiItem.quotedItem?.comment && !editing && <ChatPreviewComment>{apiItem.quotedItem.comment}</ChatPreviewComment>}
    </>
  )
}
